import { useEffect, useState } from "react";
import api, { fileUrl } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Users, Plus, Pencil, X, Power, PowerOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Gated from "@/components/Gated";
import MasterTable from "@/components/MasterTable";

const ROLES = ["Delivery Agent", "Driver", "Loader", "Supervisor", "Manager"];
const EMPTY = { name: "", role: "Delivery Agent", phone: "", email: "",
                branch_id: "", address: "", joining_date: "", active: true };

export default function DeliveryStaffSection() {
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    try {
      const [d, b] = await Promise.all([
        api.get("/delivery-staff"),
        api.get("/branches").catch(() => ({ data: [] })),
      ]);
      setItems(d.data); setBranches(b.data || []);
    } catch (_e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const branchName = (bid) => branches.find((b) => b.id === bid)?.name || "—";

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.phone.trim()) return toast.error("Mobile number is required");
    if (!form.branch_id) return toast.error("Branch is required");
    try {
      if (editId) await api.patch(`/delivery-staff/${editId}`, form);
      else await api.post("/delivery-staff", form);
      toast.success(editId ? "Staff updated" : "Staff added");
      setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const openEdit = (s) => { setEditId(s.id); setForm({ ...EMPTY, ...s }); setOpen(true); };
  const toggleActive = async (s) => {
    try {
      await api.patch(`/delivery-staff/${s.id}/active`, null, { params: { active: !s.active } });
      toast.success(`${s.name} is now ${!s.active ? "Active" : "Inactive"}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const rows = items.map((s) => ({ ...s, branch_name: branchName(s.branch_id) }));

  const columns = [
    { key: "staff_id", label: "Staff ID", render: (r) => (
        <span className="mono text-[11px] bg-primary/10 text-primary px-1.5 py-0.5 rounded font-bold">{r.staff_id || "—"}</span>
    )},
    { key: "name", label: "Name", render: (r) => (
        <div>
          <div className="font-semibold">{r.name}</div>
          {r.photo_url && <div className="text-[10px] text-muted-foreground">Photo uploaded</div>}
        </div>
    )},
    { key: "role", label: "Role" },
    { key: "phone", label: "Mobile", className: "font-mono text-xs" },
    { key: "email", label: "Email", className: "text-xs" },
    { key: "branch_name", label: "Branch" },
    { key: "joining_date", label: "Joined", className: "mono text-xs" },
    { key: "active", label: "Status", render: (r) => (
        r.active
          ? <span className="text-[9px] uppercase font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 px-1.5 py-0.5 rounded">Active</span>
          : <span className="text-[9px] uppercase font-bold bg-slate-100 text-slate-700 border border-slate-300 px-1.5 py-0.5 rounded">Inactive</span>
    )},
    { key: "actions", label: "", sortable: false, className: "text-right whitespace-nowrap", render: (r) => (
        <div className="inline-flex items-center gap-3">
          <button data-testid={`ds-toggle-${r.id}`} onClick={() => toggleActive(r)}
                  className="text-muted-foreground hover:text-primary" title={r.active ? "Deactivate" : "Activate"}>
            {r.active ? <PowerOff size={13} /> : <Power size={13} />}
          </button>
          <button data-testid={`ds-edit-${r.id}`} onClick={() => openEdit(r)}
                  className="text-muted-foreground hover:text-primary" title="Edit">
            <Pencil size={13} />
          </button>
        </div>
    )},
  ];

  return (
    <Gated feature="delivery_staff">
      <section className="border border-border rounded-md bg-card p-6 card-elev" data-testid="delivery-staff-section">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="overline flex items-center gap-2"><Users size={12} /> Master data</div>
            <h2 className="font-display text-xl mt-1">Delivery Staff</h2>
            <p className="text-sm text-muted-foreground mt-1">Auto-generated Staff ID (DS-####). Assign to pickups &amp; deliveries.</p>
          </div>
          <Button data-testid="ds-new"
                  onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }}
                  className="rounded-md bg-primary text-primary-foreground">
            <Plus size={13} className="mr-1" /> Add staff
          </Button>
        </div>

        <MasterTable
          columns={columns}
          rows={rows}
          searchKeys={["staff_id", "name", "phone", "email", "role", "branch_name"]}
          getRowKey={(r) => r.id}
          testidPrefix="ds"
          defaultSort={{ key: "staff_id", dir: "asc" }}
          emptyText="No delivery staff match your filters."
        />

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="rounded-md max-w-lg" data-testid="ds-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{editId ? "Edit staff" : "Add delivery staff"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-3">
              {editId && (
                <div className="text-[11px] mono text-muted-foreground">
                  Staff ID: <span className="font-bold text-primary">{form.staff_id}</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="overline">Staff Name *</span>
                  <Input data-testid="ds-name" value={form.name}
                         onChange={(e) => setForm({ ...form, name: e.target.value })}
                         required className="mt-1 rounded-md" />
                </label>
                <label className="block">
                  <span className="overline">Role *</span>
                  <select data-testid="ds-role" value={form.role}
                          onChange={(e) => setForm({ ...form, role: e.target.value })}
                          className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm" required>
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="overline">Mobile *</span>
                  <Input data-testid="ds-phone" value={form.phone}
                         onChange={(e) => setForm({ ...form, phone: e.target.value })}
                         required className="mt-1 rounded-md font-mono" />
                </label>
                <label className="block">
                  <span className="overline">Email</span>
                  <Input data-testid="ds-email" type="email" value={form.email}
                         onChange={(e) => setForm({ ...form, email: e.target.value })}
                         className="mt-1 rounded-md" />
                </label>
                <label className="block">
                  <span className="overline">Branch *</span>
                  <select data-testid="ds-branch" value={form.branch_id}
                          onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
                          required
                          className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                    <option value="">Select branch…</option>
                    {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="overline">Joining Date</span>
                  <Input data-testid="ds-joining" type="date" value={form.joining_date || ""}
                         onChange={(e) => setForm({ ...form, joining_date: e.target.value })}
                         className="mt-1 rounded-md" />
                </label>
              </div>
              <label className="block">
                <span className="overline">Address</span>
                <textarea data-testid="ds-address" rows={2}
                          value={form.address || ""}
                          onChange={(e) => setForm({ ...form, address: e.target.value })}
                          className="mt-1 w-full rounded-md border border-input p-2 text-sm bg-background" />
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form.active}
                       onChange={(e) => setForm({ ...form, active: e.target.checked })}
                       className="accent-[#BE1E1E]" data-testid="ds-active" />
                <span>Active</span>
              </label>
              <div className="flex justify-end gap-2 pt-1">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-md">
                  <X size={13} className="mr-1" />Cancel
                </Button>
                <Button type="submit" data-testid="ds-submit" className="rounded-md bg-primary text-primary-foreground">
                  Save
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </section>
    </Gated>
  );
}
